import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';

// Swipeable container that holds both content and menu
const Container = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: ${props => props.borderRadius || '12px'};
`;

// Content wrapper that slides left to reveal menu
const ContentWrapper = styled.div`
  position: relative;
  width: 100%;
  transform: translateX(${props => props.isMenuOpen ? `-${props.menuWidth}px` : '0'});
  transition: transform ${props => props.animationDuration || '0.3s'} cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 2;
`;

// Hidden menu that appears from the right
const MenuWrapper = styled.div`
  position: absolute;
  top: 0;
  right: -${props => props.menuWidth}px;
  width: ${props => props.menuWidth}px;
  height: 100%;
  background: ${props => props.menuBackground || props.theme.colors.background};
  border-left: 1px solid ${props => props.theme.colors.border};
  display: flex;
  flex-direction: column;
  justify-content: center;
  z-index: 1;
  
  transform: translateX(${props => props.isMenuOpen ? `-${props.menuWidth}px` : '0'});
  transition: transform ${props => props.animationDuration || '0.3s'} cubic-bezier(0.4, 0, 0.2, 1);
`;

// Overlay to close menu when tapping outside
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: transparent;
  z-index: 1;
  display: ${props => props.isVisible ? 'block' : 'none'};
`;

const SwipeContainer = ({
  children,
  menu,
  isMenuOpen,
  onMenuToggle,
  onMenuClose,
  menuWidth = 200,
  borderRadius,
  animationDuration,
  menuBackground,
  swipeThreshold = 50,
  enableSwipe = true,
  className
}) => {
  const containerRef = useRef(null);
  const [startX, setStartX] = useState(0);
  const [currentX, setCurrentX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // Touch event handlers for swipe
  const handleTouchStart = (e) => {
    if (!enableSwipe) return;
    setStartX(e.touches[0].clientX);
    setCurrentX(e.touches[0].clientX);
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!enableSwipe || !isDragging) return;
    setCurrentX(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!enableSwipe || !isDragging) return;
    
    const deltaX = startX - currentX;
    
    if (deltaX > swipeThreshold && !isMenuOpen) {
      // Swiped left - open menu
      onMenuToggle?.(true);
    } else if (deltaX < -swipeThreshold && isMenuOpen) {
      // Swiped right - close menu
      onMenuToggle?.(false);
    }
    
    setIsDragging(false);
    setStartX(0);
    setCurrentX(0);
  };

  // Mouse event handlers for desktop support
  const handleMouseDown = useCallback((e) => {
    if (!enableSwipe) return;
    setStartX(e.clientX);
    setCurrentX(e.clientX);
    setIsDragging(true);
  }, [enableSwipe]);

  const handleMouseMove = useCallback((e) => {
    if (!enableSwipe || !isDragging) return;
    setCurrentX(e.clientX);
  }, [enableSwipe, isDragging]);

  const handleMouseUp = useCallback(() => {
    if (!enableSwipe || !isDragging) return;
    
    const deltaX = startX - currentX;
    
    if (deltaX > swipeThreshold && !isMenuOpen) {
      onMenuToggle?.(true);
    } else if (deltaX < -swipeThreshold && isMenuOpen) {
      onMenuToggle?.(false);
    }
    
    setIsDragging(false);
    setStartX(0);
    setCurrentX(0);
  }, [enableSwipe, isDragging, startX, currentX, swipeThreshold, isMenuOpen, onMenuToggle]);

  // Close menu when clicking outside
  const handleOverlayClick = () => {
    onMenuClose?.();
  };

  // Add global mouse event listeners when dragging
  useEffect(() => {
    if (isDragging) {
      const handleGlobalMouseMove = (e) => handleMouseMove(e);
      const handleGlobalMouseUp = () => handleMouseUp();
      
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleGlobalMouseMove);
        document.removeEventListener('mouseup', handleGlobalMouseUp);
      };
    }
  }, [isDragging]);

  return (
    <>
      <Container
        ref={containerRef}
        className={className}
        borderRadius={borderRadius}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
      >
        <ContentWrapper
          isMenuOpen={isMenuOpen}
          menuWidth={menuWidth}
          animationDuration={animationDuration}
        >
          {children}
        </ContentWrapper>
        
        {menu && (
          <MenuWrapper
            isMenuOpen={isMenuOpen}
            menuWidth={menuWidth}
            animationDuration={animationDuration}
            menuBackground={menuBackground}
          >
            {menu}
          </MenuWrapper>
        )}
      </Container>
      
      <Overlay
        isVisible={isMenuOpen}
        onClick={handleOverlayClick}
      />
    </>
  );
};

export default SwipeContainer;
